import React, { useState, useEffect, useRef, useCallback } from "react";
import { PauseIcon, PlayIcon, CheckIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import TimerSurfaceService from "../services/TimerSurfaceService";
import "./TimerModal.css";
import "./animations/TimerAnimations.css";

// Helper function to format the timer string
const formatTimerString = (hours, minutes, seconds, milliseconds) => {
  const result =
    (hours ? `${hours}h ` : "") +
    (hours || minutes ? `${minutes}m ` : "") + // No leading zero for minutes if hours exist
    (hours || minutes || seconds ? `${seconds}s ` : "") + // No leading zero for seconds if hours or minutes exist
    (milliseconds !== null && milliseconds !== undefined ? `${String(milliseconds).padStart(2, "0")}` : "");

  return result.trim() || "0s"; // Ensure "0s" is returned if the result is empty
};

const TIC_AUDIO_URL = '/sounds/tic.mp3';
const DING_AUDIO_URL = '/sounds/ding.mp3';

export const getTimerProgressWidth = (value, target) => (
  target > 0 ? `${Math.min((value / target) * 100, 100).toFixed(1)}%` : '0%'
);

export const isTimerTargetReached = (value, target) => target > 0 && value >= target;

export const getTimerDisplayClassName = elapsed => (
  elapsed >= 3600 ? 'timer-display has-hours' : 'timer-display'
);

const TimerModal = ({ initialValue, onApply, close, startImmediately = false, imageSrc, title }) => {
  // The centisecond readout and the progress bar are written straight to the DOM
  // below, so React state only tracks what the rest of the UI actually needs:
  // whole seconds and whether the target has been passed.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [reachedTarget, setReachedTarget] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(startImmediately);
  const hasDinged = useRef(false);
  const dingAudioRef = useRef(null);
  const ticAudioRef = useRef(null);
  const lastTicSecond = useRef(null);
  const startTimeRef = useRef(null);
  const accumulatedTimeRef = useRef(0);
  const timerValueRef = useRef(0);
  const displayRef = useRef(null);
  const progressRef = useRef(null);
  const lastProgressRef = useRef(null);
  // Whether the native background timer surface (Android PiP / iOS Live
  // Activity) has been started for this modal instance. Stays false until
  // the timer first runs, so a timer that's opened but never started never
  // touches the native bridge.
  const nativeSurfaceStartedRef = useRef(false);

  const showGoalAchieved = initialValue > 0 && reachedTarget;

  const targetHours = Math.floor(initialValue / 3600000);
  const targetMinutes = Math.floor((initialValue % 3600000) / 60000);
  const targetSeconds = Math.floor((initialValue % 60000) / 1000);

  const timerHours = Math.floor(elapsedSeconds / 3600);
  const timerMinutes = Math.floor((elapsedSeconds % 3600) / 60);
  const timerSeconds = elapsedSeconds % 60;

  const paintTimer = useCallback((value) => {
    if (displayRef.current) {
      displayRef.current.textContent = formatTimerString(
        Math.floor(value / 3600000),
        Math.floor((value % 3600000) / 60000),
        Math.floor((value % 60000) / 1000),
        Math.floor((value % 1000) / 10)
      );
    }
    if (progressRef.current) {
      // Rounded because width changes cost a layout pass, and a tenth of a
      // percent is already below what the bar can show.
      const width = getTimerProgressWidth(value, initialValue);
      if (width !== lastProgressRef.current) {
        lastProgressRef.current = width;
        progressRef.current.style.width = width;
      }
    }
  }, [initialValue]);

  // Pushes the current absolute-time state to the native surface. Called
  // only on meaningful transitions (start/pause/resume/reset/stop) — never
  // per animation frame — because native derives the live readout itself
  // from startedAtEpochMs/accumulatedDurationMs and can't drift while
  // backgrounded or suspended.
  const syncNativeSurface = useCallback((running, startedAtEpochMs) => {
    const state = {
      title,
      targetDurationMs: initialValue,
      accumulatedDurationMs: accumulatedTimeRef.current,
      startedAtEpochMs: running ? startedAtEpochMs : null,
      running,
    };
    if (!nativeSurfaceStartedRef.current) {
      nativeSurfaceStartedRef.current = true;
      TimerSurfaceService.start(state);
    } else {
      TimerSurfaceService.update(state);
    }
  }, [title, initialValue]);

  const handleTimerPauseResume = () => {
    setIsTimerRunning(!isTimerRunning);
  };

  const handleTimerReset = () => {
    accumulatedTimeRef.current = 0;
    timerValueRef.current = 0;
    startTimeRef.current = isTimerRunning ? Date.now() : null;
    hasDinged.current = false;
    setElapsedSeconds(0);
    setReachedTarget(false);
    paintTimer(0);
    // Re-arm the native surface at zero — a reset mid-run should not leave
    // the Live Activity/PiP surface showing stale progress toward the goal.
    if (nativeSurfaceStartedRef.current) {
      syncNativeSurface(isTimerRunning, startTimeRef.current);
    }
  };

  const handleTimerCheckTimer = () => {
    onApply(timerValueRef.current);
    close();
  };

  const handleTimerCheckTarget = () => {
    onApply(initialValue);
    close();
  };

  useEffect(() => {
    if (!isTimerRunning) {
      if (startTimeRef.current !== null) {
        accumulatedTimeRef.current += Date.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
      // Only push a "paused" update if the surface is already showing —
      // an idle timer that's never been started has nothing to freeze.
      if (nativeSurfaceStartedRef.current) {
        syncNativeSurface(false, null);
      }
      return undefined;
    }

    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now();
    }
    syncNativeSurface(true, startTimeRef.current);

    let frame = null;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      const value = accumulatedTimeRef.current + (Date.now() - startTimeRef.current);
      timerValueRef.current = value;
      paintTimer(value);
      // Both no-op on the frames where the value is unchanged, so this only
      // actually re-renders once a second.
      setElapsedSeconds(Math.floor(value / 1000));
      setReachedTarget(isTimerTargetReached(value, initialValue));
    };
    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [isTimerRunning, initialValue, paintTimer, syncNativeSurface]);

  // The readout is not React-managed, so repaint after any render that changed it.
  useEffect(() => {
    paintTimer(timerValueRef.current);
  });

  // Tear down the native surface whenever this modal instance goes away —
  // covers closing, saving (onApply + close()), and any other unmount path,
  // since ModalService unmounts the component on close either way.
  useEffect(() => () => {
    if (nativeSurfaceStartedRef.current) {
      TimerSurfaceService.stop();
    }
  }, []);

  // Android only: fired when the native PiP overlay (or its ding scheduler)
  // has already declared the target reached and played the completion sound
  // itself. The WebView is hidden and its timers paused while PiP is up (see
  // TimerSurfacePlugin.java's rationale — rAF/HTMLAudio cannot be trusted to
  // keep running in a paused Activity), so JS cannot have dinged already;
  // mark it dinged now so the ding effect below doesn't replay it once the
  // WebView resumes and its own rAF loop independently notices the target
  // was reached. No-ops harmlessly on web/iOS via the facade's inert
  // addListener() fallback.
  useEffect(() => {
    const subscription = TimerSurfaceService.addListener('nativeTimerCompleted', () => {
      hasDinged.current = true;
      setReachedTarget(true);
    });
    return () => subscription.remove();
  }, []);

  // Play tic sound for last 3 seconds before ding
  useEffect(() => {
    const value = timerValueRef.current;
    if (value < initialValue - 3000 || value >= initialValue) {
      lastTicSecond.current = null;
      return;
    }
    if (initialValue <= 0 || !isTimerRunning) return;

    const secondsLeft = Math.ceil((initialValue - value) / 1000);
    if (lastTicSecond.current === secondsLeft) return;
    try {
      if (!ticAudioRef.current) {
        ticAudioRef.current = new window.Audio(TIC_AUDIO_URL);
      }
      ticAudioRef.current.currentTime = 0;
      ticAudioRef.current.play();
    } catch (e) {
      // Fallback: do nothing if audio fails
    }
    lastTicSecond.current = secondsLeft;
  }, [elapsedSeconds, initialValue, isTimerRunning]);

  // Play a ding sound when the target is reached (only once)
  useEffect(() => {
    if (initialValue <= 0) return;
    if (!reachedTarget) {
      hasDinged.current = false;
      return;
    }
    if (hasDinged.current) return;
    try {
      if (!dingAudioRef.current) {
        dingAudioRef.current = new window.Audio(DING_AUDIO_URL);
      }
      // Always reset to start in case of rapid reuse
      dingAudioRef.current.currentTime = 0;
      const playback = dingAudioRef.current.play();
      if (playback && typeof playback.then === 'function') {
        playback
          .then(() => TimerSurfaceService.markCompletionHandled())
          .catch(() => {});
      } else {
        // Some test/native media shims expose a synchronous play() contract.
        TimerSurfaceService.markCompletionHandled();
      }
    } catch (e) {
      // Keep the native fallback armed if foreground audio cannot play.
    }
    hasDinged.current = true;
  }, [reachedTarget, initialValue]);

  return (
    <div className={`timer-modal timer-modal-anim ${showGoalAchieved ? 'goal-achieved' : ''}`}>
      {/* Background Progress Fill */}
      <div 
        ref={progressRef}
        className={`timer-progress-fill ${reachedTarget ? 'is-complete' : ''} ${isTimerRunning ? 'timer-progress-bar-shimmer' : ''}`}
      />

      <div className="timer-modal-content">
        <div className="timer-header">
             {title && <h3 className="timer-title">{title}</h3>}
             {/* Conditionally display the target time only if initialValue > 0 */}
             {initialValue > 0 && (
              <div className="timer-target-pill">
                 <span style={{opacity: 0.6}}>TARGET</span>
                 {formatTimerString(targetHours, targetMinutes, targetSeconds, 0)}
              </div>
             )}
             {initialValue > 0 && (
              <div
               className={`goal-achieved-banner ${showGoalAchieved ? 'is-visible' : ''}`}
               style={{ width: '90%', margin: '0.5rem auto' }}
               role="status"
               aria-hidden={!showGoalAchieved}
              >
                <span className="goal-achieved-text">🏆 Goal Achieved! 🏆</span>
              </div>
             )}
        </div>
        <div className={getTimerDisplayClassName(elapsedSeconds)}>
          <div ref={displayRef} className="timer-display-value" />
        </div>
        {imageSrc && (
          <div className="timer-demo">
             <img 
                src={imageSrc} 
                alt="Exercise" 
                className="timer-demo-image"
             />
          </div>
        )}
        
        {!imageSrc && <div style={{ flex: 1 }}></div>}

        <div className="timer-controls-row">
          <button className="timer-reset-button" onClick={handleTimerReset} aria-label="Reset timer">
            <ArrowPathIcon className="icon" />
          </button>
          <div className="timer-check-buttons">
            <button
              className="timer-check-button accept-timer"
              onClick={handleTimerCheckTimer}
              aria-label="Save elapsed time"
            >
              <CheckIcon className="icon" />
              <div className="timer-elapsed-apply-label">
                {formatTimerString(timerHours, timerMinutes, timerSeconds)}
              </div>
            </button>
            {initialValue > 0 && (
              <button
                className={`timer-check-button ${reachedTarget ? 'goal-unlocked' : ''}`}
                onClick={handleTimerCheckTarget}
                disabled={!reachedTarget}
                aria-label="Save target time"
              >
                <CheckIcon className="icon" />
                <span className="timer-check-button-label">
                  {formatTimerString(targetHours, targetMinutes, targetSeconds)}
                </span>
              </button>
            )}
          </div>
          <button className={`timer-pause-button ${isTimerRunning ? 'is-running' : ''}`} onClick={handleTimerPauseResume} aria-label={isTimerRunning ? 'Pause timer' : 'Start timer'}>
            {isTimerRunning ? <PauseIcon className="icon" /> : <PlayIcon className="icon" />}
          </button>
        </div>
      </div>
    </div>
  );
};

TimerModal.displayName = 'TimerModal';
TimerModal.modalLayout = 'edge-to-edge';

export default TimerModal;
